import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getQueryParams } from "@/utils/httpRequest";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { baseUrl, onPrepareHeaders } from "./helpers";
import { showNotification } from "../actions/notification";
import { lenderCallLogService } from "./lenderCallLogService";
import { setProcessingState } from "../reducers/modalConfirmation";

export const lenderCallLogNoteService = createApi({
  reducerPath: "lenderCallLogNoteApi",
  tagTypes: ["LenderCallLogNote"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLenderCallLogNotes: builder.query({
      query: ({ loanId, lenderCallLogId, ...params }) =>
        `loans/${loanId}/lender-call-logs/${lenderCallLogId}/notes?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ lenderCallLogNoteId }) => ({ type: "LenderCallLogNote", lenderCallLogNoteId })),
              { type: "LenderCallLogNote", id: "LIST" },
            ]
          : [{ type: "LenderCallLogNote", id: "LIST" }],
    }),
    addLenderCallLogNote: builder.mutation({
      query: ({ loanId, lenderCallLogId, ...body }) => ({
        url: `loans/${loanId}/lender-call-logs/${lenderCallLogId}/notes`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LenderCallLogNote", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_CALL_LOG_NOTE_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note added to the call log!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderCallLogService.util.invalidateTags([{ type: "LenderCallLog", id: "LIST" }]));
        } catch (err) {
          /* empty */
        }
      },
    }),
    updateLenderCallLogNote: builder.mutation({
      query: ({ loanId, lenderCallLogId, lenderCallLogNoteId, ...body }) => ({
        url: `loans/${loanId}/lender-call-logs/${lenderCallLogId}/notes/${lenderCallLogNoteId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "LenderCallLogNote", id }],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_CALL_LOG_NOTE_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    deleteLenderCallLogNote: builder.mutation({
      query({ loanId, lenderCallLogId, lenderCallLogNoteId }) {
        return {
          url: `loans/${loanId}/lender-call-logs/${lenderCallLogId}/notes/${lenderCallLogNoteId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "LenderCallLogNote", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_CALL_LOG_NOTE_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been deleted from call log!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderCallLogService.util.invalidateTags([{ type: "LenderCallLog", id: "LIST" }]));
        } catch (err) {
          dispatch(setProcessingState(false));
        }
      },
    }),
  }),
});

export const {
  useGetLenderCallLogNotesQuery,
  useAddLenderCallLogNoteMutation,
  useUpdateLenderCallLogNoteMutation,
  useDeleteLenderCallLogNoteMutation,
} = lenderCallLogNoteService;
